// CSV export/import matching the server's `geargrams_core::csv` (same
// headers, LighterPack aliases, unit by name or abbreviation).
import { UNITS, unitById } from "./units";

export type CsvRow = {
  name: string;
  description: string;
  url: string;
  category: string;
  grams: number;
  unit_id: number;
  quantity: number | null;
  worn: boolean;
  consumable: boolean;
};

const HEADER_LIST = ["name", "description", "url", "category", "weight", "unit", "quantity", "worn", "consumable"];
const HEADER_LIBRARY = ["name", "description", "url", "category", "weight", "unit", "worn", "consumable"];

function q(s: string): string {
  return `"${s.replace(/"/g, '""')}"`;
}

function weightText(row: CsvRow): string {
  const v = row.grams / unitById(row.unit_id).grams;
  return String(v);
}

export function exportCsv(rows: CsvRow[], withQuantity: boolean): string {
  const lines = [(withQuantity ? HEADER_LIST : HEADER_LIBRARY).map(q).join(",")];
  for (const r of rows) {
    const rec = [r.name, r.description, r.url, r.category, weightText(r), unitById(r.unit_id).name];
    if (withQuantity) rec.push(String(r.quantity ?? 1));
    rec.push(String(r.worn), String(r.consumable));
    lines.push(rec.map(q).join(","));
  }
  return lines.join("\n") + "\n";
}

/** RFC 4180-ish parser: quotes, escaped quotes, CRLF, trailing newline. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += c;
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

function parseUnit(s: string): number {
  const t = s.trim().toLowerCase();
  const alias: Record<string, string> = { kilogram: "kg", kilograms: "kg", kgs: "kg", gram: "grams", g: "grams", pounds: "pound", lbs: "pound", ounces: "ounce" };
  const n = alias[t] ?? t;
  return UNITS.find((u) => u.name === n || u.abbreviation === n)?.id ?? 1;
}

function parseBool(s: string): boolean {
  return ["true", "1", "yes", "y", "x"].includes(s.trim().toLowerCase());
}

export function importCsv(text: string): CsvRow[] {
  const rows = parseCsv(text);
  if (rows.length === 0) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const col = (names: string[]) => {
    const i = headers.findIndex((h) => names.includes(h));
    return i < 0 ? null : i;
  };
  const c = {
    name: col(["name", "item name"]),
    desc: col(["description", "desc"]),
    url: col(["url"]),
    cat: col(["category"]),
    weight: col(["weight"]),
    unit: col(["unit"]),
    qty: col(["quantity", "qty"]),
    worn: col(["worn"]),
    cons: col(["consumable"]),
  };
  if (Object.values(c).every((v) => v === null)) return [];
  const get = (rec: string[], i: number | null) => (i === null ? "" : (rec[i] ?? "").trim());
  const out: CsvRow[] = [];
  for (const rec of rows.slice(1)) {
    if (rec.every((f) => f.trim() === "")) continue;
    const unit_id = parseUnit(get(rec, c.unit));
    const weight = parseFloat(get(rec, c.weight)) || 0;
    const qtyText = get(rec, c.qty);
    out.push({
      name: get(rec, c.name).slice(0, 255),
      description: get(rec, c.desc).slice(0, 255),
      url: get(rec, c.url),
      category: get(rec, c.cat).slice(0, 255),
      grams: Math.max(0, weight) * unitById(unit_id).grams,
      unit_id,
      quantity: c.qty === null ? null : parseFloat(qtyText) || 1,
      worn: parseBool(get(rec, c.worn)),
      consumable: parseBool(get(rec, c.cons)),
    });
  }
  return out;
}
